import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { CloudInasResponse } from 'app/_models/result';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { MatCard, MatCardHeader, MatCardContent } from '@angular/material/card';
import { CalcConfigService } from '_services/calc-config.service';

interface DisplayRow {
  key: string;
  draftValue: string | number | null;
  publishedValue: string | number | null;
  class: 'subtotal' | 'total' | 'single-row' | '';
}

type InasValues = Record<string, unknown>;

interface NeedAnalysisData {
  MiscCalculations?: InasValues;
  NeedAnalysisResultSet?: {
    NeedAnalysisResult?: Array<InasValues & { ResultType?: number }>;
    [key: string]: unknown;
  };
}

const IGNORED_KEYS = new Set([
  'ResultType',
  'Methodology',
  'UniqueProcessingIdentifier',
  'InasVersion',
  'ValueOfOtherAssets',
]);

const MISC_KEYS = new Set(['EfmSAI', 'MetPovertyGuidelines']);

const DISPLAY_KEY_FIXES: Record<string, string> = {
  'Efm SAI': 'EFM SAI',
  'Im Allocation Percent': 'Number in College Adjustment',
  'Ira Retirement': 'IRA Retirement',
  'Aesa Allowance': 'AESA Allowance',
  'Cesa Allowance': 'CESA Allowance',
  'USIncome Tax': 'US Income Tax',
};

const ROW_CLASS: Record<string, DisplayRow['class']> = {
  TotalIncome: 'subtotal',
  TotalAllowances: 'subtotal',
  NetWorth: 'subtotal',
  AvailableIncome: 'total',
  DiscretionaryNetWorth: 'total',
  ContributionForStudent: 'total',
  ContributionFromIncome: 'single-row',
};

@Component({
    selector: 'app-inas-comparison',
    templateUrl: './inas-comparison.component.html',
    styleUrls: ['./inas-comparison.component.css'],
    imports: [MatCard, MatCardHeader, MatCardContent, NgTemplateOutlet, NgClass]
})
export class InasComparisonComponent implements OnChanges, OnInit {
  private readonly cc = inject(CalcConfigService);
  private readonly institutionalMethod = toSignal(
    this.cc.institutionalMethod$.pipe(map(value => value ?? false)),
    { initialValue: false },
  );

  @Input() draft?: CloudInasResponse;
  @Input() published?: CloudInasResponse;

  resultTypes: { typeName: string, displayObjects: DisplayRow[]; }[] = [];
  miscCalcs: DisplayRow[] = [];
  imResults: DisplayRow[] = [];

  ngOnInit(): void {
    this.cc.institutionalMethod$.pipe(takeUntilDestroyed()).subscribe(() => this.rebuild());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['draft'] && !changes['published']) {
      return;
    }
    this.rebuild();
  }

  private rebuild(): void {
    const institutionalMethod = this.institutionalMethod();

    this.resultTypes = [];
    this.imResults = [];
    this.miscCalcs = this.populateObjects('MiscCalculations');

    const efmParents = this.populateObjects('EfmParent', 5);
    if (efmParents.length > 0) {
      if (institutionalMethod) {
        this.resultTypes.push({ typeName: 'IM Parents', displayObjects: this.populateObjects('ImParent', 2) });
      }
      this.resultTypes.push({ typeName: 'EFM Parents', displayObjects: efmParents });
    }

    if (institutionalMethod) {
      this.resultTypes.push({ typeName: 'IM Student', displayObjects: this.populateObjects('ImStudent', 4) });
    }
    this.resultTypes.push({ typeName: 'EFM Student', displayObjects: this.populateObjects('EfmStudent', 6) });

    if (institutionalMethod) {
      const imParents = this.resultTypes
        .find(x => x.typeName === 'IM Parents')
        ?.displayObjects.find(x => x.key === 'Contribution For Student');
      const imStudent = this.resultTypes
        .find(x => x.typeName === 'IM Student')
        ?.displayObjects.find(x => x.key === 'Contribution For Student');

      if (imParents && imStudent) {
        this.imResults = [
          {
            key: 'Parent Contribution for Student',
            draftValue: imParents.draftValue,
            publishedValue: imParents.publishedValue,
            class: ''
          },
          {
            key: 'Student Contribution for Student',
            draftValue: imStudent.draftValue,
            publishedValue: imStudent.publishedValue,
            class: ''
          }
        ];
      }
    }
  }

  private populateObjects(keyName: string, resultType?: number): DisplayRow[] {
    return compareInasValues(
      this.resultValues(this.draft, keyName, resultType),
      this.resultValues(this.published, keyName, resultType),
      keyName === 'MiscCalculations'
    );
  }

  private resultValues(response: CloudInasResponse | undefined, keyName: string, resultType?: number): InasValues | undefined {
    const data = needAnalysisData(response);
    if (!data) {
      return undefined;
    }
    if (keyName === 'MiscCalculations') {
      return asValues(data.MiscCalculations);
    }
    const set = data.NeedAnalysisResultSet;
    return asValues(set?.[keyName])
      ?? asValues(set?.NeedAnalysisResult?.find(row => row.ResultType === resultType));
  }
}

function needAnalysisData(response: CloudInasResponse | undefined): NeedAnalysisData | undefined {
  const data = response?.RunNeedAnalysisResult?.NeedAnalysisData as NeedAnalysisData | NeedAnalysisData[] | undefined;
  if (!data) {
    return undefined;
  }
  return Array.isArray(data) ? data[0] : data;
}

function asValues(value: unknown): InasValues | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as InasValues;
  }
  return undefined;
}

function compareInasValues(
  draft: InasValues | undefined,
  published: InasValues | undefined,
  miscOnly: boolean
): DisplayRow[] {
  if (!draft || !published) {
    return [];
  }

  const keys = miscOnly
    ? (hasKey(draft, published, 'EfmSAI') ? [...MISC_KEYS] : [])
    : orderedKeys(draft, published).filter(key => !IGNORED_KEYS.has(key));

  return keys.map(key => ({
    key: displayKey(key),
    draftValue: formatValue(draft[key]),
    publishedValue: formatValue(published[key]),
    class: ROW_CLASS[key] ?? ''
  }));
}

function hasKey(draft: InasValues, published: InasValues, key: string): boolean {
  return Object.hasOwn(draft, key) || Object.hasOwn(published, key);
}

function orderedKeys(draft: InasValues, published: InasValues): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const key of [...Object.keys(draft), ...Object.keys(published)]) {
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    keys.push(key);
  }
  return keys;
}

function displayKey(key: string): string {
  const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2');
  return DISPLAY_KEY_FIXES[spaced] ?? spaced;
}

function formatValue(value: unknown): string | number | null {
  if (typeof value === 'number') {
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  return String(value);
}
